import { forwardRef, type SelectHTMLAttributes } from 'react'

import { cva, type VariantProps } from 'class-variance-authority'
import { ChevronDown } from 'lucide-react'

import { cn } from '@/lib/utils'

const selectVariants = cva(
  'appearance-none border bg-white text-gray-700 outline-none transition-all disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'border-gray-200 focus:border-[#2258B5] focus:ring-2 focus:ring-[#2258B5]/10',
        banner: 'border-white/30 focus:border-white focus:ring-2 focus:ring-white/20',
      },
      shape: {
        rounded: 'rounded-full',
        default: 'rounded-lg',
      },
      size: {
        xs: 'py-0.5 pl-2 pr-6 text-[10px]',
        sm: 'py-1.5 pl-3 pr-7 text-xs',
        md: 'py-2 pl-4 pr-8 text-sm',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      shape: 'rounded',
      size: 'md',
      fullWidth: false,
    },
  },
)

const ICON_SIZE: Record<string, number> = { xs: 11, sm: 12, md: 13 }
const ICON_RIGHT: Record<string, string> = { xs: 'right-1.5', sm: 'right-2', md: 'right-3' }

interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'>,
    VariantProps<typeof selectVariants> {
  wrapperClassName?: string
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, variant, shape, size = 'md', fullWidth, wrapperClassName, children, ...props }, ref) => {
    const iconSize = ICON_SIZE[size ?? 'md'] ?? 13
    const iconRight = ICON_RIGHT[size ?? 'md']

    return (
      <div className={cn('relative', fullWidth && 'w-full', wrapperClassName)}>
        <select
          ref={ref}
          className={cn(selectVariants({ variant, shape, size, fullWidth }), className)}
          {...props}
        >
          {children}
        </select>
        <ChevronDown
          size={iconSize}
          className={cn('pointer-events-none absolute top-1/2 -translate-y-1/2 text-gray-400', iconRight)}
        />
      </div>
    )
  },
)

Select.displayName = 'Select'

export { Select, selectVariants }
export type { SelectProps }
